import './styles.css';

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDebounceCallback } from '@react-hook/debounce';
import useInterval from '@use-it/interval';

import { usegetVirtualDirectoryQuery } from '../../apollo';
import QuestionIcon from '../../assets/icons/question.svg';
import SearchIcon from '../../assets/icons/search.png';
import { colors } from '../../assets/styles';
import {
  Footer,
  Icon,
  LoadingSpinner,
  VirtualDirectoryContactList,
  VirtualDirectoryContactRow,
  VirtualDirectoryInfoModal
} from '../../components';
import { CHECK_ACCESS_INTERVAL_MS } from '../../config';
import { useAnonymousTokenPayload } from '../../hooks';
import { getIsLeasingOfficeClosed } from './getIsLeasingOfficeClosed';
import styles from './styles';

const VirtualDirectory: React.FC = () => {
  const accessPointId = useParams().accessPointId as string;
  const navigate = useNavigate();
  const vdExp = useAnonymousTokenPayload()?.vdExp; // When access to VD expires

  const isAuthorized = (): boolean => {
    try {
      if (!vdExp || typeof vdExp !== 'number') {
        return false;
      }

      const vdExpMs = vdExp * 1000; // Convert seconds to milliseconds

      if (Date.now() >= vdExpMs) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [authorized, setAuthorized] = useState<boolean>(isAuthorized());
  const [isLeasingOfficeClosed, setIsLeasingOfficeClosed] = useState<boolean>(false);
  const [search, setSearch] = useState<string | null>(null);
  const debouncedSetSearch = useDebounceCallback(setSearch, 500);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const { data, loading, error: queryError } = usegetVirtualDirectoryQuery({
    skip: !accessPointId || !authorized,
    variables: {
      input: { accessPointId, search: search || null },
    },
    onCompleted: ({ virtualDirectoryPublic }) => {
      setIsLeasingOfficeClosed(getIsLeasingOfficeClosed(virtualDirectoryPublic.property));
    },
  });

  const property = data?.virtualDirectoryPublic.property || null;
  const virtualDirectoryUsers = data?.virtualDirectoryPublic.listedUsers || [];
  const leasingOfficeDisplayName = property?.propertyVirtualDirectory.leasingOfficeDisplayName || (
    'Leasing Office'
  );

  useInterval(() => {
    setAuthorized(isAuthorized());
    setIsLeasingOfficeClosed(getIsLeasingOfficeClosed(property));
  }, CHECK_ACCESS_INTERVAL_MS);

  const error = !authorized
    ? new Error('Please re-scan the QR code to continue using the property directory')
    : queryError;

  return (
    <>
      <div className={styles.header}>
        <div
          className={styles.headerLeft}
          onClick={() => navigate(-1)}
        >
          <div className={styles.headerLeftIcon}>
            <Icon.ArrowLeft color={colors.white} />
          </div>
        </div>
        <div className={styles.headerText}>
          Property Directory
        </div>
        <div className={styles.headerRight}>
          <button className={styles.buttonWithIcon} onClick={() => setInfoModalOpen(true)}>
            <img src={QuestionIcon} alt="" className={styles.infoIcon} />
          </button>
        </div>
      </div>
      <div className={styles.contactsLookupWrapper}>
        <div className={styles.displayFlexWrapper}>
          <input
            className={styles.searchBar}
            style={{
              background: `url(${SearchIcon}) 5px no-repeat`,
              backgroundSize: 20
            }}
            type="text"
            placeholder={'Search by resident name or unit number'}
            onChange={e => debouncedSetSearch(e.target.value)}
          />
        </div>
      </div>
      <div className={styles.contactsWrapper}>
        {loading && <LoadingSpinner />}
        {!loading && (
          <>
            {!search && property?.propertyVirtualDirectory.leasingOfficePhoneNumber && (
              <VirtualDirectoryContactRow
                key="leasing-office"
                accessPointId={accessPointId}
                contactName={leasingOfficeDisplayName}
                disabled={isLeasingOfficeClosed}
                actionContent={isLeasingOfficeClosed
                  ? <span style={{ color: '#333' }}>CLOSED</span>
                  : undefined
                }
              />
            )}
            <VirtualDirectoryContactList
              accessPointId={accessPointId}
              contacts={virtualDirectoryUsers}
              error={error}
            />
          </>
        )}
      </div>
      <VirtualDirectoryInfoModal
        closeModal={() => setInfoModalOpen(false)}
        isOpen={infoModalOpen}
        supportPhoneNumber={property?.supportPhoneNumber}
        leasingOfficeDisplayName={leasingOfficeDisplayName}
      />
      <Footer />
    </>
  );
};

export default VirtualDirectory;
